import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { formatDate } from 'src/utils/string/stringUtil';

import { WMS2090_R01HeaderDto } from './Dto_WMS2090_R01';
import { useWMS2090_R01Store } from './Store_WMS2090_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';
import { cellDateComparator } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'WMS2090_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<WMS2090_R01HeaderDto> | null>;
	handleCreateClick: () => void;
	defaultWMS2090_R01HeaderRow: WMS2090_R01HeaderDto;
}

export const Table_WMS2090_R01 = (props: Props) => {
	const { gridRef, handleCreateClick, defaultWMS2090_R01HeaderRow } = props;

	const {
		WMS2090_R01List,
		selectedWMS2090_R01,
		setSelectedWMS2090_R01,
		initSelectedWMS2090_R01,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
		setDetailScrollTop,
	} = useWMS2090_R01Store();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'transportNo',
		gridData: WMS2090_R01List,
		defaultGridData: defaultWMS2090_R01HeaderRow,
		selectedRow: selectedWMS2090_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		compNm: '사업장',
		transportDt: '입고일자',
		customNm: '상호',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compNm')(params), []);

	const baseColumnDefs: ColDef<WMS2090_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '사업장', colId: 'compNm', field: 'compNm', width: 110,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'compNm'),
		},
		{
			headerName: '입고일자', colId: 'transportDt', field: 'transportDt', width: 90,
			valueFormatter: (p: { data?: WMS2090_R01HeaderDto }) =>
				p.data?.transportDt ? formatDate(new Date(p.data.transportDt)) : '',
			comparator: cellDateComparator,
			cellStyle: (p) => getCellStyle(p, 'transportDt'),
		},
		{
			headerName: '상호', colId: 'customNm', field: 'customNm', width: 180,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'customNm'),
		},
	], [headerNames]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<WMS2090_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<WMS2090_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<WMS2090_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const activeElement = document.activeElement;
		if (activeElement instanceof HTMLElement) {
			activeElement.blur();
		}

		const isSelected = selectedWMS2090_R01?.transportNo === rowData.transportNo;
		if (isSelected) {
			setSelectedWMS2090_R01(undefined);
			handleCreateClick();
		} else {
			setSelectedWMS2090_R01(rowData);
		}
		setDetailScrollTop(null);
	}, [selectedWMS2090_R01, setSelectedWMS2090_R01, handleCreateClick, setDetailScrollTop]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		compNm: `${WMS2090_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [WMS2090_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [WMS2090_R01List, selectedWMS2090_R01]);

	return (
		<GridContextWrapper<WMS2090_R01HeaderDto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedWMS2090_R01}
			defaultRowData={defaultWMS2090_R01HeaderRow}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<WMS2090_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<WMS2090_R01HeaderDto>({
					idKey: 'transportNo',
					selectedList: selectedWMS2090_R01 ? [selectedWMS2090_R01.transportNo] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
